import isNil from "lodash/isNil";
import { Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { isProjects } from "PFApp/booking/utils/calendar_root.utils";
import { BulkOperationFilter } from "PFCore/services/bookings_bulk_operation";
import { Id } from "PFTypes";

import { RowEventContext, SelectionEvent, SelectModeData } from "../booking_edit_mode_store.types";
import {
  computeRowSelectionFilters,
  computeUnselectRowBookings,
  removeActivityProfileFilter,
  removeBookingFromExclude,
  removeBookingFromFilters
} from "./selection";

const computeRowSelectionFilter = (row: RowEventContext): BulkOperationFilter =>
  computeRowSelectionFilters({
    rowType: row.type,
    rowId: row.originalId,
    calendarRoot: row.calendarRoot,
    parentIds: row.parentIds,
    isLinkedToEngagement: row.isLinkedToEngagement ?? false,
    isAllBookingsModeEnabled: row.isAllBookingsModeEnabled ?? false,
    bookingCategoryIds: row.bookingCategoryIds
  });

export const applySelectRowEvent = (
  selection: SelectModeData,
  row: RowEventContext,
  bookingsToReselect?: Id[]
): SelectModeData => {
  const rowFilter = computeRowSelectionFilter(row);

  let newSelection: SelectModeData = {
    ...selection,
    filters: [...selection.filters, rowFilter]
  };

  if (bookingsToReselect && bookingsToReselect.length > 0) {
    const currentExclude = newSelection.exclude?.id || [];
    const remainingExclude = currentExclude.filter((id) => !bookingsToReselect.includes(id));

    newSelection = {
      ...newSelection,
      exclude: remainingExclude.length > 0 ? { id: remainingExclude } : {}
    };
  }

  return newSelection;
};

export const applyUnselectRowEvent = (
  selection: SelectModeData,
  row: RowEventContext,
  bookingsToDeselect?: Id[],
  isSelectAllMode?: boolean
): SelectModeData => {
  const isProjectsMode = isProjects(row.calendarRoot);

  const isActivityRowAndIncludedInFilters =
    [isProjectsMode && RowType.Engagement, RowType.Role, RowType.Category].includes(row.type) &&
    selection?.filters.some(({ activityId, profileId }) => activityId === row.originalId && isNil(profileId));

  const isProfileRowOrRoleInWorkforceRow =
    (isProjectsMode && row.type === RowType.Profile) || (!isProjectsMode && row.type === RowType.Role);

  if (isActivityRowAndIncludedInFilters) {
    selection = {
      ...selection,
      filters: removeActivityProfileFilter(selection.filters, row.originalId)
    };
  } else if (isProfileRowOrRoleInWorkforceRow) {
    const parentId = row.parentIds[row.parentIds.length - 1];
    if (
      selection?.filters.some(({ activityId, profileId }) =>
        isProjectsMode
          ? activityId === parentId && profileId === row.originalId
          : activityId === row.originalId && profileId === parentId
      )
    ) {
      const ids: [Id, Id] = isProjectsMode ? [parentId, row.originalId] : [row.originalId, parentId];
      selection = {
        ...selection,
        filters: removeActivityProfileFilter(selection.filters, ...ids)
      };
    }
  }

  if (bookingsToDeselect) {
    const result = computeUnselectRowBookings({
      bookingIds: bookingsToDeselect,
      currentSelection: selection,
      isSelectAllMode: !!isSelectAllMode
    });

    if (result.strategy === "removeFromFilters" && result.newSelection) {
      selection = result.newSelection;
    } else if (result.strategy === "addToExclude" && result.bookingsToExclude) {
      const currentExclude = selection.exclude?.id || [];
      const newExclude = [...currentExclude, ...result.bookingsToExclude];

      selection = {
        ...selection,
        exclude: { id: newExclude }
      };
    }
  }

  return selection;
};

const applySelectBookingEvent = (selection: SelectModeData, bookingId: Id): SelectModeData => {
  const isInExclude = selection.exclude?.id?.includes(bookingId);

  if (isInExclude) {
    return {
      ...selection,
      exclude: removeBookingFromExclude(selection.exclude, bookingId)
    };
  } else {
    return {
      filters: [...selection.filters, { id: [bookingId] }],
      exclude: selection.exclude
    };
  }
};

const applyUnselectBookingEvent = (selection: SelectModeData, bookingId: Id): SelectModeData => {
  const isInFilters = selection.filters.some((filter) => filter.id?.includes(bookingId));

  if (isInFilters) {
    return {
      filters: removeBookingFromFilters(selection.filters, bookingId),
      exclude: selection.exclude
    };
  } else {
    return {
      ...selection,
      exclude: { id: [...(selection.exclude?.id ?? []), bookingId] }
    };
  }
};

export const applyEventIncremental = (
  currentSelection: SelectModeData,
  currentRows: Map<string, Row>,
  event: SelectionEvent
): { selection: SelectModeData; rowsMap: Map<string, Row> } => {
  let selection = currentSelection;
  const rowsMap = new Map(currentRows);

  switch (event.type) {
    case "SELECT_ROW": {
      selection = applySelectRowEvent(selection, event.row, event.bookingsToReselect);

      const row: Row = {
        key: event.row.key,
        type: event.row.type,
        original: { id: event.row.originalId } as any,
        parents: event.row.parents
      } as Row;

      for (const [key, existingRow] of rowsMap.entries()) {
        if (existingRow.parents.includes(event.row.key)) {
          rowsMap.delete(key);
        }
      }

      rowsMap.set(event.row.key, row);
      break;
    }

    case "UNSELECT_ROW": {
      selection = applyUnselectRowEvent(
        selection,
        event.row,
        event.bookingsToDeselect,
        event.isSelectAllMode
      );
      rowsMap.delete(event.row.key);
      break;
    }

    case "SELECT_BOOKING": {
      selection = applySelectBookingEvent(selection, event.bookingId);
      break;
    }

    case "UNSELECT_BOOKING": {
      selection = applyUnselectBookingEvent(selection, event.bookingId);
      break;
    }

    case "CLEAR_SELECTION": {
      selection = { filters: [] };
      rowsMap.clear();
      break;
    }
  }

  return { selection, rowsMap };
};
